import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { getToken } from '../../services/authToken';
import { useLanguage } from '../../contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Adapts backend progress array to frontend shape.
 * Backend: [{module_id, slug, title, completed_lessons, total_lessons,
 *            percent, last_activity_at, status}]
 */
function adaptProgressArray(arr) {
  const totalCompleted = arr.reduce((sum, m) => sum + (m.completed_lessons || 0), 0);
  const totalLessons = arr.reduce((sum, m) => sum + (m.total_lessons || 0), 0);
  const overallPct = totalLessons > 0 ? Math.round((totalCompleted / totalLessons) * 100) : 0;

  // Find most recent activity date
  const lastActivities = arr
    .filter((m) => m.last_activity_at)
    .map((m) => m.last_activity_at)
    .sort()
    .reverse();

  return {
    // summary fields
    totalMiles: 0,
    streak: 0,
    completedLessons: totalCompleted,
    totalLessons,
    overallProgress: overallPct,
    totalTimeMinutes: 0,
    level: '—',
    lastActivityAt: lastActivities[0] || null,
    // detailed module list (raw backend fields preserved)
    modules: arr.map((m) => ({
      id: m.module_id || m.slug,
      module_id: m.module_id,
      slug: m.slug,
      title: m.title,
      completed_lessons: m.completed_lessons || 0,
      total_lessons: m.total_lessons || 0,
      percent: Math.round(m.percent ?? 0),
      progress: Math.round(m.percent ?? 0),
      last_activity_at: m.last_activity_at,
      status: m.status || 'not_started',
      order: m.order ?? 99,
    })),
    // not in backend progress endpoint — filled by stats hook
    weeklyActivity: [],
    skills: [],
    recentActivity: [],
  };
}

async function fetchCabinProgress(lang = 'tr') {
  const token = getToken();
  const res = await axios.get(`${API}/cabin/progress?lang=${lang}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (Array.isArray(res.data)) {
    return adaptProgressArray(res.data);
  }
  return res.data;
}

export function useCabinProgress() {
  const { lang } = useLanguage();
  return useQuery({
    queryKey: ['cabin', 'progress', lang],
    queryFn: () => fetchCabinProgress(lang),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    enabled: typeof window !== 'undefined' && !!getToken(),
  });
}
